/**
 * PairUpToday — Complete Verification Center Types
 * Phase 1: Identity Documents (Aadhaar, PAN, Voter ID, Driving Licence, Passport)
 * Future-Ready Architecture: Selfie, Face Match, Bank Verification, KYC Provider
 */

export type VerificationOverallStatus =
  | 'not_started'
  | 'in_progress'
  | 'submitted'
  | 'under_review'
  | 'verified'
  | 'rejected';

export type CompanionProfileLifecycleStatus =
  | 'draft'
  | 'documents_incomplete'
  | 'documents_uploaded'
  | 'under_review'
  | 'verified'
  | 'rejected'
  | 'suspended';

export type DocumentVerificationStatus =
  | 'not_uploaded'
  | 'uploaded'
  | 'pending'
  | 'verified'
  | 'rejected'
  | 'reupload_required';

export type SupportedDocumentType =
  | 'aadhaar'
  | 'pan'
  | 'voter_id'
  | 'driving_license'
  | 'passport';

export interface DocumentSideData {
  uploaded: boolean;
  storagePath?: string;
  downloadUrl?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt?: string;
}

export interface DocumentBackSideData extends DocumentSideData {
  required: boolean;
  notApplicable?: boolean;
}

export interface CompanionDocumentRecord {
  id: string; // documentType
  documentType: SupportedDocumentType;
  title: string;
  description: string;
  badge: string;
  idFormatHint: string;
  front: DocumentSideData;
  back: DocumentBackSideData;
  status: DocumentVerificationStatus;
  rejectionReason?: string | null;
  adminComment?: string | null;
  idNumber?: string;
  legalName?: string;
  uploadedAt?: string;
  updatedAt?: string;
  verifiedAt?: string | null;
  verifiedBy?: string | null;
}

export interface CompanionVerificationProfile {
  companionId: string;
  name?: string;
  email?: string;
  phone?: string;
  city?: string;
  avatarUrl?: string;
  profileStatus?: CompanionProfileLifecycleStatus;
  verificationStatus: VerificationOverallStatus;
  identityStatus: DocumentVerificationStatus;
  selfieStatus: 'not_started' | 'pending' | 'verified' | 'rejected';
  faceStatus: 'not_started' | 'pending' | 'verified' | 'rejected';
  bankStatus: 'not_started' | 'pending' | 'verified' | 'review_required' | 'failed' | 'unavailable' | 'rejected';
  bankVerification?: {
    status: 'verified' | 'pending' | 'review_required' | 'failed' | 'unavailable';
    maskedAccount: string;
    ifsc: string;
    providerRequestId: string | null;
    method: string;
    checkedAt: string;
    verifiedAt: string | null;
  };
  kycStatus: 'not_started' | 'pending' | 'verified' | 'rejected';
  verificationProgress: number; // 0 to 100
  isVerified: boolean;
  submittedAt?: string;
  verifiedAt?: string | null;
  verifiedBy?: string | null;
  rejectionReason?: string | null;
  updatedAt?: string;
  documents?: Record<SupportedDocumentType, CompanionDocumentRecord>;
}

export interface VerificationAuditEvent {
  eventId: string;
  companionId: string;
  documentType?: SupportedDocumentType | string;
  eventType: 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'REUPLOAD_REQUESTED';
  adminEmail: string;
  notes?: string;
  timestamp: string;
}

/**
 * Central configuration: Frontend verification logic is NOT hard-coded.
 * In Phase 1: only identity is required.
 * In future phases: selfie, face, bank, kyc can be switched to true seamlessly.
 */
export const verificationRequirements = {
  identity: true,
  selfie: false,
  face: false,
  bank: false,
  kyc: false,
};

export const DOCUMENT_TYPE_METADATA: Record<
  SupportedDocumentType,
  {
    title: string;
    subtitle: string;
    badge: string;
    backRequired: boolean;
    idPlaceholder: string;
    helpText: string;
  }
> = {
  aadhaar: {
    title: 'Aadhaar Card',
    subtitle: '12-digit UIDAI National Identity Card',
    badge: 'Front + Back Required',
    backRequired: true,
    idPlaceholder: 'XXXX-XXXX-XXXX',
    helpText: 'Front must show clear face and 12-digit number; Back must show address and QR code.',
  },
  pan: {
    title: 'PAN Card',
    subtitle: '10-digit Permanent Account Number (Income Tax Dept)',
    badge: 'Front Side Only',
    backRequired: false,
    idPlaceholder: 'ABCDE1234F',
    helpText: 'Must show clear photograph, PAN alphanumeric number, and father’s name.',
  },
  voter_id: {
    title: 'Voter ID (EPIC)',
    subtitle: 'Election Commission of India Identity Card',
    badge: 'Front + Back Required',
    backRequired: true,
    idPlaceholder: 'ABC1234567',
    helpText: 'Front must show photo & EPIC number; Back must show registered constituency address.',
  },
  driving_license: {
    title: 'Driving Licence',
    subtitle: 'State Transport Department Government of India',
    badge: 'Front + Back Required',
    backRequired: true,
    idPlaceholder: 'DL-01-20220019284',
    helpText: 'Front must show driver license number, issue date; Back must show vehicle classes.',
  },
  passport: {
    title: 'Passport',
    subtitle: 'Republic of India — Ministry of External Affairs',
    badge: 'Data Page + Address Page',
    backRequired: true,
    idPlaceholder: 'Z1234567',
    helpText: 'Page 1 must show photo and MRZ; Page 2 must show permanent address and parents’ name.',
  },
};
